import { SubmitClaim } from "commonAssets/components/Forms/SubmitClaim";
import { withLoginRecaptcha } from "../loginRecaptcha.js";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import { createElement } from "commonAssets/lib/createElement";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { loadMember } from "commonAssets/lib/loadMember";
import { ConfirmEmailAddress } from "commonAssets/lib/ConfirmEmailAddress";
import { modal } from "commonAssets/lib/modal";
import { i18n, outputDiv, loading } from "commonAssets/js/constants";
import { validateField } from "commonAssets/lib/validateField";
import { clearMessage } from "commonAssets/lib/clearMessage";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { uniformClaimDeadline } from "commonAssets/lib/uniformClaimDeadline";
import formDefault from "../../docs/formUpdateEmail.json";

function getCmDataSubclasses(resultData) {
    const cmDataRaw =
        resultData?.class_member?.cm_data ?? resultData?.cm_data ?? null;
    if (!cmDataRaw) return [];

    let cmData = cmDataRaw;
    if (typeof cmData === "string") {
        try {
            cmData = JSON.parse(cmData);
        } catch {
            return [];
        }
    }

    const subclass = cmData?.subclass;
    if (Array.isArray(subclass)) {
        return subclass
            .filter((s) => typeof s === "string")
            .map((s) => s.toLowerCase());
    }
    if (typeof subclass === "string") return [subclass.toLowerCase()];
    return [];
}

function enforceUpdateEmailSchema(form) {
    const nextForm = structuredClone(form);

    nextForm.claim_fields = [];
    nextForm.claim_transactions = [];

    if (nextForm.claim_upload?.documentation) {
        nextForm.claim_upload.documentation.required = false;
    }

    nextForm.form_options.claim_deadline_alert.claim_form = "Update Email";

    return nextForm;
}

function updateEmailCustomInitClaimWrapper(customInitClaim) {
    return (claim) => {
        const title = document.getElementById("claimFormDeadline");
        if (title) title.textContent = "Update Email";
        return customInitClaim ? customInitClaim(claim) : claim;
    };
}

export class customUpdateEmail extends SubmitClaim {
    constructor(...args) {
        super(...args);
        // Class member logins (getClassMember) require a reCAPTCHA token.
        withLoginRecaptcha(this.claim.api);
    }

    claim_instructions(params) {
        if (
            params.form_options.claim_form.general_instructions.display ===
            false
        ) {
            return createElement("div", { class: "d-none" });
        }

        return createElement(
            "div",
            {
                class: "mb-3",
                id: "generalInstructionsWrapper",
            },
            [
                createElement(
                    "div",
                    {
                        class: "text-center fw-bold fs-3",
                    },
                    [
                        createElement(
                            "button",
                            {
                                class: "btn btn-link fs-4 fw-bold",
                                style: "color: #128f76; text-decoration: underline;",
                                type: "button",
                                "data-bs-toggle": "collapse",
                                "data-bs-target": "#generalInstructions",
                                "aria-expanded": true,
                                "aria-controls": "generalInstructions",
                            },
                            [],
                            params.form_options.claim_form.general_instructions
                                .title
                        ),
                    ]
                ),
                createElement(
                    "div",
                    {
                        class: "collapse show",
                        id: "generalInstructions",
                    },
                    [
                        createElement(
                            "div",
                            {
                                class: "bg-light text-dark border rounded p-3 mb-3",
                            },
                            [],
                            params.form_options.claim_form.general_instructions
                                .body
                        ),
                    ]
                ),
            ]
        );
    }

    init_claim(form) {
        this.claim.api
            .claimsDeadlineGet({ domain: this.claim.data.domain })
            .then((result) => {
                // Claim Deadline - Default Pacific DateTime
                let datetime;
                if (Boolean(result.data.pacific_datetime)) {
                    datetime = result.data.pacific_datetime;
                } else {
                    datetime = `<pre>Error: datetime</pre>`;
                }

                let claim_deadline;
                if (Boolean(result.data.claim_deadline)) {
                    claim_deadline = result.data.claim_deadline;
                }

                // Error Handling
                if (result.data.error) {
                    this.setup_result_data_error(result);
                    return;
                }

                const container = createElement("div", {
                    class: "display-form",
                });

                /**
                 * Claim Guard
                 */
                if (form.form_options.notice_confirmation.display === true) {
                    // Guard
                    container.appendChild(this.guard(form, datetime));
                    outputDiv.appendChild(container);

                    const zero_zero_id =
                        document.getElementById("zero_zero_id");
                    const confirmation_code =
                        document.getElementById("confirmation_code");
                    const skip_guard = document.getElementById("skip-guard");

                    // Notice Id and Confirmation Code
                    [zero_zero_id, confirmation_code].forEach(function (input) {
                        input.addEventListener("change", validateField);
                    });

                    // Skip Guard
                    if (skip_guard) {
                        skip_guard.addEventListener("click", () => {
                            // Hide guard
                            // document.getElementById('guard').classList.add("d-none");
                            document.getElementById("guard").remove();
                            // New Claim Form
                            container.appendChild(
                                this.claim_form(form, datetime)
                            );
                            container.scrollIntoView({ behavior: "smooth" });
                            outputDiv.appendChild(container);

                            // Init claim fields
                            this.init_claim_fields(
                                this.claim,
                                form,
                                claim_deadline
                            );
                        });
                    }

                    // ada compliance updates - added
                    // if input#submit-guard was clicked add .was-verified to form#guard
                    document
                        .getElementById("submit-guard")
                        .addEventListener("click", () => {
                            document
                                .getElementById("guard")
                                .classList.add("was-validated");
                            // TODO: add required text to labels
                        });

                    // Validate Guard Fields
                    document
                        .getElementById("guard")
                        .addEventListener("submit", (e) => {
                            e.preventDefault();

                            let valid = true;
                            const member = {
                                domain: this.claim.data.domain,
                                zero_zero_id: zero_zero_id.value.toUpperCase(),
                                confirmation_code: confirmation_code.value,
                            };

                            clearMessage();

                            // Validate guard fields
                            [zero_zero_id, confirmation_code].forEach(
                                function (input) {
                                    valid = validateField(input) && valid;
                                }
                            );
                            if (!valid) return;

                            loadingGif(loading, "show");

                            // Retrieve class member data (use POST to pass notice id and confirmation code privately)
                            this.claim.api
                                .classMemberPost({}, member)
                                .then((result) => {
                                    // Check for error message
                                    if (result.data.error) {
                                        toggleFieldError(
                                            zero_zero_id,
                                            true,
                                            ""
                                        );
                                        toggleFieldError(
                                            confirmation_code,
                                            true,
                                            ""
                                        );
                                        this.errorHandler.log_exception(
                                            new Error(
                                                "Error loading class member",
                                                { cause: result }
                                            )
                                        );
                                        this.errorHandler.display_modal(result);
                                        return;
                                    }

                                    // Add notice id and confirmation code to claim data
                                    this.claim.data.zero_zero_id =
                                        member.zero_zero_id;
                                    this.claim.data.confirmation_code =
                                        member.confirmation_code;

                                    // Define the environment for the OTP
                                    const environment =
                                        this.claim.data.domain.split(".")[0]; // ada compliance - added

                                    // Guard gating via cm_data.subclass:
                                    // - claim_id response may include `class_member.cm_data`
                                    // - non-claim_id response may include `cm_data`
                                    const subclasses =
                                        getCmDataSubclasses(result.data);
                                    const requiredSubclasses = [
                                        "class_member",
                                        "email_update",
                                    ];
                                    const hasAccess = requiredSubclasses.every(
                                        (key) => subclasses.includes(key)
                                    );
                                    if (!hasAccess) {
                                        loadingGif(loading, "hide", 250);
                                        modal(
                                            i18n.getTranslation("error"),
                                            `<p>This Notice ID is not eligible to update an email address.</p><p>If you believe this is an error, contact the Settlement Administrator at <a href="mailto:Info@2025PriceSettlement.com">Info@2025PriceSettlement.com</a>.</p>`
                                        );
                                        return;
                                    }

                                    if (result.data.claim_id) {
                                        this.claim.data.claim_id =
                                            result.data.claim_id;
                                        this.claim.class_member = result.data;
                                        if (environment !== "local") {
                                            this.send_otp(
                                                form,
                                                container,
                                                result.data,
                                                claim_deadline
                                            );
                                            return;
                                        }
                                    }

                                    // Populate claim form
                                    this.populate_claim_form(
                                        form,
                                        container,
                                        result.data,
                                        claim_deadline
                                    );
                                })
                                .catch((error) => {
                                    this.errorHandler.log_exception(
                                        new Error(
                                            "Error loading class member",
                                            { cause: error }
                                        )
                                    );
                                    this.errorHandler.display_modal(error);
                                });
                        });
                } else {
                    /**
                     * No Claim Guard
                     */
                    // Edit claim link
                    this.edit_claim(form);

                    // New Claim Form
                    const deadlineSpan = uniformClaimDeadline(claim_deadline);
                    container.appendChild(this.claim_form(form, deadlineSpan));
                    outputDiv.appendChild(container);

                    // Init claim fields
                    this.init_claim_fields(this.claim, form, claim_deadline);
                }

                loadingGif(loading, "hide", 250);
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching claim deadline", { cause: error })
                );
                this.errorHandler.display(error);
            });
    }

    claim_confirmation(params) {
        return createElement(
            "div",
            {
                class: "bg-light text-dark border rounded p-3 mb-3",
                id: "confirmation",
            },
            [
                createElement(
                    "h3",
                    { class: "text-center mb-3" },
                    [],
                    params.confirmation["title"]
                ),
                createElement(
                    "h4",
                    { class: "alert alert-success text-center mb-3" },
                    [],
                    "THIS IS A COPY OF YOUR UPDATE EMAIL REQUEST"
                ),
                createElement(
                    "p",
                    { class: "text-center text-danger mb-3" },
                    [],
                    params.confirmation.upload_fail
                ),
                createElement(
                    "p",
                    { class: "text-center fw-bold mb-3" },
                    [],
                    params.confirmation.print_note
                ),
                createElement(
                    "h4",
                    { class: "text-center fw-bold mb-3" },
                    [],
                    params.confirmation.claim_details.title
                ),
                createElement(
                    "p",
                    { class: "text-center fw-bold mb-3" },
                    [],
                    params.confirmation.claim_details.body
                ),
                this.confirmation_claim_member(params),
                this.confirmation_claim_fields(params),
                this.confirmation_claim_transactions(params),
                createElement(
                    "div",
                    {
                        class: "row mb-3",
                    },
                    [
                        createElement(
                            "div",
                            { class: "col-6 text-end fw-bold" },
                            [],
                            "Correlation ID"
                        ),
                        createElement(
                            "div",
                            {
                                class: "col-6",
                                id: "confirmation_correlation_id",
                            },
                            [],
                            params.confirmation.claim_details.correlation_id
                        ),
                    ]
                ),
                createElement(
                    "p",
                    { class: "text-center mb-3" },
                    [],
                    params.confirmation.claim_details.footer
                ),
                createElement(
                    "p",
                    { class: "text-center mb-3" },
                    [],
                    params.confirmation.claim_details.edit
                ),
            ]
        );
    }

    destroy_claim(claim) {
        loadingGif(loading, "hide", 500);

        if (claim.flags.success) {
            claim.data.confirmation = this.claim_data_confirmation(claim.data);
            document.querySelector("div.display-form").classList.add("d-none");

            outputDiv.appendChild(this.claim_confirmation(claim.data));
            outputDiv.scrollIntoView({ behavior: "smooth" });

            setTimeout(() => {
                modal(
                    i18n.getTranslation("notice"),
                    `<p class="fw-bold">You will receive a confirmation and copy of your Update Email request via email. Do not share your Notice ID or Confirmation Code with others. We will never call you for this information.</p><p>You may also print this screen for your records.</p><p>If you don't receive a confirmation email after 3 days, please check your spam filter.</p>`
                );
            }, 250);
        } else {
            modal(
                i18n.getTranslation("error"),
                i18n.getTranslation("claim_submit_error")
            );
        }

        for (const type in claim.inputs) {
            if (claim.inputs[type] && claim.inputs[type].length > 0) {
                claim.inputs[type].forEach(function (field) {
                    field.disabled = true;
                });
            }
        }

        [].forEach.call(
            document.querySelectorAll('input[type="radio"]'),
            function (radio) {
                radio.setAttribute("disabled", "disabled");
            }
        );

        document
            .getElementById("submit-claim")
            ?.setAttribute("disabled", "disabled");
    }

    claim_data_confirmation(claim) {
        return {
            title: "Your Update Email request has been submitted successfully.",
            upload_fail: "",
            print_note: "Please print this page for your records.",
            claim_details: {
                title: "Your Submission Details",
                correlation_id: claim.correlation_id,
                body: "Please keep the information below for your records.",
                footer:
                    "If you have any questions regarding your Update Email request, please contact the Settlement Administrator.",
                edit: "",
                excluded_keys: [
                    "Payment Token",
                    "Payment Email",
                    "Payment Phone",
                    "device_token",
                ],
            },
        };
    }

    claim_form(params, additionalParams) {
        const heading = params.form_options.claim_deadline_alert.claim_form;
        const deadlineParser = new DOMParser();
        const deadlineDocument = deadlineParser.parseFromString(
            heading,
            "text/html"
        );
        const deadlineDateSpan = deadlineDocument.querySelector("span");
        if (deadlineDateSpan !== null) {
            deadlineDateSpan.textContent = additionalParams;
        }

        return createElement(
            "form",
            {
                class: "needs-validation update-email",
                id: "form",
                method: "post",
            },
            [
                createElement(
                    "h3",
                    {
                        class: "text-center mb-3",
                        id: "claimFormDeadline",
                    },
                    [],
                    deadlineDocument.body.innerHTML
                ),
                createElement(
                    "div",
                    {
                        class: "alert alert-info text-center fw-bold text-dark",
                        id: "confirmation-email-alert",
                        role: "alert",
                    },
                    [],
                    params.form_options.claim_form.confirmation_email_notice
                ),
                this.claim_instructions(params),
                this.claim_contact_information(params),
                this.claim_certification(params),
            ]
        );
    }

    populate_claim_form(form, container, class_member, claim_deadline) {
        console.log("[populate_claim_form:update-email]");

        this.errorHandler.set_user({
            id: class_member.zero_zero_id,
            email: class_member.email_address,
        });

        document.getElementById("guard").remove();

        container.appendChild(this.claim_form(form, "Update Email"));
        container.scrollIntoView({ behavior: "smooth" });
        outputDiv.appendChild(container);

        loadingGif(loading, "hide", 250);

        loadMember(class_member);

        if (this.customLoadMember != null) {
            this.customLoadMember(this.claim);
        }

        this.init_claim_fields(this.claim, form, claim_deadline);
    }

    init_claim_fields(claim, form, claim_deadline) {
        this.init_claim_inputs();
        this.init_claim_inputs_loop();

        this.setup_phone_number_validation();
        this.setup_country_field_toggle();

        if (
            document.getElementById("email_address") &&
            document.getElementById("confirm_email_address")
        ) {
            ConfirmEmailAddress("init");
        }

        if (Boolean(document.getElementById("form"))) {
            document.getElementById("form").setAttribute("novalidate", "true");

            document.getElementById("submit-claim")?.addEventListener(
                "click",
                () => {
                    document
                        .getElementById("form")
                        .classList.add("was-validated");
                }
            );

            document
                .getElementById("form")
                .addEventListener("submit", (process) => {
                    this.process_claim(process, this.claim, claim_deadline);
                });
        }

        if (this.customInitClaim != null) {
            this.claim = this.customInitClaim(this.claim) || this.claim;
        }

        loadingGif(loading, "hide", 1000);
    }

    async form() {
        const form = enforceUpdateEmailSchema(formDefault.claim_form[this.lang]);
        this.init_claim(form);
    }

    static createInstance({
        appClaimForm,
        userLanguage,
        customInitClaim = null,
        customProcessClaim = null,
        customLoadMember = null,
        customDstProcess = null,
        errorHandler = new ErrorHandler(),
    }) {
        const noopDstProcess = (claim) => claim;
        return new customUpdateEmail(
            appClaimForm,
            userLanguage,
            updateEmailCustomInitClaimWrapper(customInitClaim),
            customProcessClaim,
            customLoadMember,
            customDstProcess ?? noopDstProcess,
            errorHandler
        );
    }
}
